import {
  EVENT_SORT_FIELDS,
  type EventListItem,
  type EventListQuery,
  type EventSortField,
  type SortOrder,
} from "@cdt/shared";
import { ArrowDown, ArrowUp, ArrowUpDown, Flag, Pencil } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { EditEventDialog } from "@/components/events/EditEventDialog";
import { StatusBadge } from "@/components/events/StatusBadge";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/ui/StarRating";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatConfidence, formatDate, formatDateTime } from "@/lib/formatters";
import { setEventPriority } from "@/lib/eventsApi";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";

interface ColumnSpec {
  key: string;
  label: string;
  sortField?: EventSortField;
  className?: string;
  cellClassName?: string;
}

const SORTABLE = new Set<string>(EVENT_SORT_FIELDS);

const COLUMNS: ColumnSpec[] = [
  {
    key: "emailReceivedAt",
    label: "Ricevuta",
    sortField: "emailReceivedAt",
    className: "w-[140px]",
  },
  { key: "emailFrom", label: "Mittente", className: "w-[200px]" },
  { key: "emailSubject", label: "Oggetto", cellClassName: "max-w-[320px]" },
  {
    key: "title",
    label: "Titolo estratto",
    sortField: "title",
    cellClassName: "max-w-[260px]",
  },
  {
    key: "startDate",
    label: "Inizio evento",
    sortField: "startDate",
    className: "w-[140px]",
  },
  { key: "status", label: "Stato", className: "w-[110px]" },
  { key: "confidence", label: "Confidenza", className: "w-[100px] text-right", cellClassName: "text-right" },
  { key: "priority", label: "Priorità", className: "w-[100px]" },
  { key: "actions", label: "", className: "w-[60px]" },
];

interface EventsTableProps {
  items: EventListItem[];
  query: EventListQuery;
  onSort: (patch: { sortBy: EventSortField; sortOrder: SortOrder }) => void;
  isFetching: boolean;
}

function nextOrder(current: SortOrder): SortOrder {
  return current === "asc" ? "desc" : "asc";
}

export function EventsTable({ items, query, onSort, isFetching }: EventsTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [editEvent, setEditEvent] = useState<EventListItem | null>(null);
  const isMobile = useIsMobile();

  const priorityMutation = useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) =>
      setEventPriority(id, priority),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError: () => {
      toast.error("Errore nell'aggiornamento della priorità");
    },
  });

  function handleSortClick(field: EventSortField) {
    if (query.sortBy === field) {
      onSort({ sortBy: field, sortOrder: nextOrder(query.sortOrder) });
    } else {
      onSort({ sortBy: field, sortOrder: "desc" });
    }
  }

  function handleNavigate(item: EventListItem) {
    navigate(`/events/${item.id}`, { state: { from: location.search } });
  }

  // ── Mobile card list ──────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div
        className={cn(
          "space-y-2 transition-opacity",
          isFetching && "opacity-70",
        )}
      >
        {items.map((item) => {
          const unseen = item.seenAt === null;
          return (
            <div
              key={item.id}
              onClick={() => handleNavigate(item)}
              className={cn(
                "cursor-pointer rounded-lg border bg-card px-4 py-3 transition-colors active:bg-muted/60",
                unseen && "border-l-2 border-l-primary bg-primary/[0.02]",
              )}
            >
              {/* Row 1: subject / title + unseen dot */}
              <div className="flex items-start gap-2">
                {unseen && (
                  <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Non visto" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={cn("truncate text-sm leading-snug", unseen && "font-semibold")}>
                    {item.title ?? item.emailSubject}
                  </p>
                  {item.title && (
                    <p className="truncate text-xs text-muted-foreground">{item.emailSubject}</p>
                  )}
                </div>
              </div>

              {/* Row 2: sender + important flag */}
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                {item.isImportant && (
                  <Flag className="h-3 w-3 shrink-0 fill-rose-500 text-rose-500" aria-label="Mittente importante" />
                )}
                <span className="truncate">{item.emailFrom}</span>
              </div>

              {/* Row 3: meta row */}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <StatusBadge status={item.status} />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateTime(item.emailReceivedAt)}
                </span>
                {item.startDate && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Evento: {formatDate(item.startDate)}
                  </span>
                )}
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatConfidence(item.confidence)}
                </span>
              </div>

              {/* Row 4: priority + edit */}
              <div
                className="mt-2 flex items-center justify-between"
                onClick={(e) => e.stopPropagation()}
              >
                <StarRating
                  value={item.priority as 0 | 1 | 2 | 3}
                  onChange={(p) => priorityMutation.mutate({ id: item.id, priority: p })}
                  size="md"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Modifica evento"
                  onClick={() => setEditEvent(item)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}

        {editEvent && (
          <EditEventDialog
            key={`table-edit-${editEvent.id}-${editEvent.updatedAt}`}
            event={editEvent}
            open={true}
            onOpenChange={(open) => {
              if (!open) setEditEvent(null);
            }}
          />
        )}
      </div>
    );
  }

  // ── Desktop table ─────────────────────────────────────────────────────────
  return (
    <div
      className={cn(
        "rounded-lg border bg-card transition-opacity",
        isFetching && "opacity-70",
      )}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((col) => {
              const sortable = col.sortField && SORTABLE.has(col.sortField);
              const active = col.sortField && query.sortBy === col.sortField;
              return (
                <TableHead key={col.key} className={col.className}>
                  {sortable && col.sortField ? (
                    <button
                      type="button"
                      onClick={() => handleSortClick(col.sortField!)}
                      className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
                    >
                      {col.label}
                      {active ? (
                        query.sortOrder === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </button>
                  ) : (
                    col.label
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const unseen = item.seenAt === null;
            return (
            <TableRow
              key={item.id}
              onClick={() => handleNavigate(item)}
              className={cn(
                "cursor-pointer",
                unseen && "border-l-2 border-l-primary bg-primary/[0.02]",
              )}
            >
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  {unseen && (
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-full bg-primary"
                      aria-label="Non visto"
                    />
                  )}
                  {formatDateTime(item.emailReceivedAt)}
                </div>
              </TableCell>
              <TableCell className="truncate" title={item.emailFrom}>
                <span className="inline-flex items-center gap-1.5">
                  {item.isImportant && (
                    <Flag className="h-3.5 w-3.5 shrink-0 fill-rose-500 text-rose-500" aria-label="Mittente importante" />
                  )}
                  <span className={cn(unseen && "font-medium")}>{item.emailFrom}</span>
                </span>
              </TableCell>
              <TableCell className="max-w-[320px] truncate" title={item.emailSubject}>
                <span className={cn(unseen && "font-medium")}>{item.emailSubject}</span>
              </TableCell>
              <TableCell
                className="max-w-[260px] truncate"
                title={item.title ?? undefined}
              >
                {item.title ?? <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                {formatDate(item.startDate)}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <StatusBadge status={item.status} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-right text-xs tabular-nums">
                {formatConfidence(item.confidence)}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <StarRating
                  value={item.priority as 0 | 1 | 2 | 3}
                  onChange={(p) =>
                    priorityMutation.mutate({ id: item.id, priority: p })
                  }
                  size="md"
                />
              </TableCell>
              <TableCell
                onClick={(e) => e.stopPropagation()}
                className="text-right"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Modifica evento"
                  onClick={() => setEditEvent(item)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {editEvent && (
        <EditEventDialog
          key={`table-edit-${editEvent.id}-${editEvent.updatedAt}`}
          event={editEvent}
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditEvent(null);
          }}
        />
      )}
    </div>
  );
}
